/**
 * Task 6.4 — `GET organizations/:organizationId/performance/overview`
 * (Task 6.3). Permission `stats_org_read` OR `finance_stats_read` (any of
 * the two); which block(s) come back is decided server-side per caller — a
 * block the caller may not read is ABSENT, never empty. The project's usual
 * `{ data }` envelope, like `bookingStats.api.ts` and unlike the older
 * finance-stats endpoints.
 */
import type {
  PerformanceClientsResponse,
  PerformanceDailyRevenueResponse,
  PerformanceLeaderboardsResponse,
  PerformanceRetentionResponse,
  PerformanceMembershipsResponse,
  PerformanceOverviewResponse,
  PerformancePaymentMethodsResponse,
  PerformanceRevenueResponse,
  PerformanceTrainerWorkResponse,
} from '@mfw/common';
import type { BaseApiResponse } from './types/api.types';
import type {
  PerformanceClientsArgs,
  PerformanceRetentionRangeArgs,
  PerformanceOverviewRangeArgs,
} from './types/performanceStats.types';

import { get } from './axios';

export const fetchPerformanceOverview = async ({
  from,
  organizationId,
  to,
}: PerformanceOverviewRangeArgs) => {
  const response = await get<BaseApiResponse<PerformanceOverviewResponse>>(
    `/v2/organizations/${organizationId}/performance/overview`,
    // The query DTO is `.strict()` — only `from`/`to`, both bare civil days.
    { query: { from, to } },
  );
  return response?.data ?? null;
};

/**
 * Task 6.7 — `GET organizations/:organizationId/performance/retention`
 * (Task 6.6). Same envelope and the same strict `from`/`to` query DTO as
 * `fetchPerformanceOverview`.
 */
export const fetchPerformanceRetention = async ({
  from,
  organizationId,
  to,
}: PerformanceRetentionRangeArgs) => {
  const response = await get<BaseApiResponse<PerformanceRetentionResponse>>(
    `/v2/organizations/${organizationId}/performance/retention`,
    { query: { from, to } },
  );
  return response?.data ?? null;
};

/**
 * PO 08.10. — `GET organizations/:organizationId/performance/revenue`
 * (`finance_stats_read`; 403 for an employed trainer). Same strict
 * `from`/`to` civil-day query as the Overview.
 */
export const fetchPerformanceRevenue = async ({
  from,
  organizationId,
  to,
}: PerformanceOverviewRangeArgs) => {
  const response = await get<BaseApiResponse<PerformanceRevenueResponse>>(
    `/v2/organizations/${organizationId}/performance/revenue`,
    { query: { from, to } },
  );
  return response?.data ?? null;
};

/** PO 08.10. — `GET organizations/:organizationId/performance/memberships`
 *  (`stats_org_read` OR `finance_stats_read`; no money). */
export const fetchPerformanceMemberships = async ({
  from,
  organizationId,
  to,
}: PerformanceOverviewRangeArgs) => {
  const response = await get<BaseApiResponse<PerformanceMembershipsResponse>>(
    `/v2/organizations/${organizationId}/performance/memberships`,
    { query: { from, to } },
  );
  return response?.data ?? null;
};

/**
 * PO 08.10.2026 — `GET organizations/:organizationId/performance/clients`.
 * `stats_org_read`; money comes back only for a caller allowed to read it
 * (`moneyVisible`). An empty filter is left out of the query (`undefined`
 * keys are dropped by `queryParamsFromString`); `trainingTypes` goes as a
 * repeated key.
 */
export const fetchPerformanceClients = async ({
  currency,
  from,
  limit,
  list,
  membershipId,
  offset,
  order,
  organizationId,
  sort,
  to,
  trainerId,
  trainingTypes,
}: PerformanceClientsArgs) => {
  const response = await get<BaseApiResponse<PerformanceClientsResponse>>(
    `/v2/organizations/${organizationId}/performance/clients`,
    {
      query: {
        currency,
        from,
        limit: String(limit),
        list,
        membershipId: membershipId || undefined,
        offset: String(offset),
        order,
        sort,
        to,
        trainerId: trainerId || undefined,
        // repeated keys; the BE DTO normalizes one or many to a list
        trainingTypes: trainingTypes?.length ? trainingTypes : undefined,
      },
    },
  );
  return response?.data ?? null;
};

/** PO 08.10.2026 — `GET …/performance/payment-methods` (`finance_stats_read`;
 *  403 for an employed trainer). */
export const fetchPerformancePaymentMethods = async ({
  from,
  organizationId,
  to,
}: PerformanceOverviewRangeArgs) => {
  const response = await get<
    BaseApiResponse<PerformancePaymentMethodsResponse>
  >(`/v2/organizations/${organizationId}/performance/payment-methods`, {
    query: { from, to },
  });
  return response?.data ?? null;
};

/** PO 08.10.2026 — `GET …/performance/daily-revenue` (`finance_stats_read`;
 *  403 for an employed trainer). */
export const fetchPerformanceDailyRevenue = async ({
  from,
  organizationId,
  to,
}: PerformanceOverviewRangeArgs) => {
  const response = await get<BaseApiResponse<PerformanceDailyRevenueResponse>>(
    `/v2/organizations/${organizationId}/performance/daily-revenue`,
    { query: { from, to } },
  );
  return response?.data ?? null;
};

/** PO 08.10.2026 (1.7) — `GET …/performance/leaderboards` (either key;
 *  money only for a reader who may see it). */
export const fetchPerformanceLeaderboards = async ({
  from,
  organizationId,
  to,
}: PerformanceOverviewRangeArgs) => {
  const response = await get<BaseApiResponse<PerformanceLeaderboardsResponse>>(
    `/v2/organizations/${organizationId}/performance/leaderboards`,
    { query: { from, to } },
  );
  return response?.data ?? null;
};

/** PO 08.10.2026 (5.2) — `GET …/performance/trainer-work` (either key; money
 *  only for a reader who may see it). */
export const fetchPerformanceTrainerWork = async ({
  from,
  organizationId,
  to,
}: PerformanceOverviewRangeArgs) => {
  const response = await get<BaseApiResponse<PerformanceTrainerWorkResponse>>(
    `/v2/organizations/${organizationId}/performance/trainer-work`,
    { query: { from, to } },
  );
  return response?.data ?? null;
};
